// Plain JavaScript version of your Stimulus controller

const DISABLED_CLASS_NAME = "tvsu-pagination__link-disable";
const ACTIVE_CLASS_NAME = "tvsu-active";
const CONTROLLER = "data-news-target";
const TARGETS = [
  "prev",
  "next",
  "last",
  "current",
  "total",
  "size",
  "count",
  "breadcrumbs",
  "all", // all tags
  "except", // except all tags
];

const getElement = (name) => {
  return document.querySelector(`[${CONTROLLER}~="${name}"]`);
};

const getElements = (name) => {
  return document.querySelectorAll(`[${CONTROLLER}~="${name}"]`);
};

export class NewsPaginator {
  constructor() {
    if (this.connectTargets()) {
      this.connect();
    }
  }

  destroy() {
    this.unbindActions();
  }

  connectTargets() {
    this.contentTarget = getElement("content");
    if (!this.contentTarget) {
      return false;
    }

    // targets
    TARGETS.forEach((selector) => {
      this[`${selector}Target`] = getElement(selector);
    });
    this.tagItemsTargets = Array.from(getElements("tag"));
    return true;
  }

  connect() {
    // navigation handler bound once
    this._navHandler = this.navigate.bind(this);
    this.newsPage = !!this.currentTarget;
    // only initialize if we have a content area
    this.init();
    this.bindActions();
  }

  init() {
    const lastPage = parseInt(this.totalTarget?.textContent, 10) || 0;
    const { page, tag, exceptTag } = this.getUrlParams();
    this.updatePagination(page, lastPage);
    this.setActiveTag(tag, exceptTag);
  }

  bindActions() {
    [this.prevTarget, this.nextTarget, this.lastTarget].forEach((el) =>
      el?.addEventListener("click", this._navHandler),
    );
    this.tagItemsTargets.forEach((el) =>
      el.addEventListener("click", this._navHandler),
    );
  }

  unbindActions() {
    [this.prevTarget, this.nextTarget, this.lastTarget].forEach((el) =>
      el?.removeEventListener("click", this._navHandler),
    );
    this.tagItemsTargets.forEach((el) =>
      el.removeEventListener("click", this._navHandler),
    );
  }

  navigate(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const url = link.getAttribute("href");

    if (this.newsPage) {
      window.history.pushState({}, "", url);
    }

    this.fetchAndUpdate(url, link);
  }

  fetchAndUpdate(url, link = null) {
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => {
        if (!r.ok) throw new Error(`Network error ${r.status}`);
        return r.json();
      })
      .then((data) => {
        // if called from popstate, need a dummy link to recompute params
        const triggerHref = link?.getAttribute("href") || url;
        this.updateContent(data, triggerHref);
        this.scrollToBreadcrumbs();
      })
      .catch((err) => console.error("Fetch failed:", err));
  }

  updateContent(data, href) {
    this.contentTarget.innerHTML = "";
    data.news.forEach((item) => {
      const newsHtml = this.createNewsHtml(item);
      this.contentTarget.appendChild(newsHtml);
    });

    const lastPage = data.lastPage;
    const { page, tag, exceptTag } = this.getUrlParams(href);

    this.updatePagination(page, lastPage);
    this.setActiveTag(tag, exceptTag);
    this.updateCounts(data.newsSize, data.totalNews, href);
  }

  updateCounts(size, total, href) {
    this.sizeTarget.textContent = size;
    this.countTarget.textContent = total;
    this.countTarget.setAttribute("href", href);
  }

  scrollToBreadcrumbs() {
    if (this.newsPage) {
      this.breadcrumbsTarget?.scrollIntoView({ behavior: "smooth" });
    }
  }

  buildUrl(overrides = {}) {
    const params = new URLSearchParams({
      ...this.getUrlParams().urlParams,
      ...overrides,
    });
    return `${window.location.pathname}?${params.toString()}`;
  }

  getUrlParams(search = window.location.search) {
    const base = window.location.origin;
    const url = new URL(search, base);

    const params = url.searchParams;
    return {
      urlParams: Object.fromEntries(params.entries()),
      page: parseInt(params.get("page"), 10) || 1,
      tag: params.get("tag"),
      exceptTag: params.get("tag_except"),
    };
  }

  setActiveTag(tag, exceptTag) {
    this.tagItemsTargets.forEach((el) =>
      el.classList.remove(ACTIVE_CLASS_NAME),
    );
    if (exceptTag) {
      this.exceptTarget?.classList.add(ACTIVE_CLASS_NAME);
      return;
    }
    if (tag) {
      this.tagItemsTargets
        .find((el) => el.dataset.tag === tag)
        ?.classList.add(ACTIVE_CLASS_NAME);
    } else {
      this.allTarget?.classList.add(ACTIVE_CLASS_NAME);
    }
  }

  updatePagination(current, total) {
    // main page don`t have page buttons
    if (!this.prevTarget) {
      return;
    }

    this.prevTarget.classList.toggle(DISABLED_CLASS_NAME, current <= 1);
    this.nextTarget.classList.toggle(DISABLED_CLASS_NAME, current >= total);
    this.lastTarget.classList.toggle(DISABLED_CLASS_NAME, current >= total);

    this.prevTarget.setAttribute("href", this.buildUrl({ page: current - 1 }));
    this.nextTarget.setAttribute("href", this.buildUrl({ page: current + 1 }));
    this.lastTarget.setAttribute("href", this.buildUrl({ page: total }));

    this.currentTarget.textContent = current;
    this.totalTarget.textContent = total;
  }

  createNewsHtml(news) {
    const href = document.createElement("a");
    href.setAttribute("href", `${news.url}`);
    href.classList.add("tvsu-news-item");
    if (news.image) {
      href.classList.add("tvsu-news-item-image");
      href.style.backgroundImage = `url(${JSON.stringify(news.image)})`;
    }

    const title = document.createElement("h5");
    title.classList.add("tvsu-news-item__title");
    title.innerText = news.title;

    const date = document.createElement("span");
    date.classList.add("tvsu-news-item__date");
    date.innerText = news.date;
    const info = document.createElement("div");
    info.classList.add("tvsu-news-item__info");

    const content = document.createElement("div");
    content.classList.add("tvsu-news-item__content");

    info.appendChild(date);
    content.appendChild(title);
    content.appendChild(info);
    href.appendChild(content);

    return href;
  }
}
